import { FC } from "react";
import BuilderActiveClientsStack from "./shared/BuilderActiveClientsStack";
import TranslationDropdown from "./TranslationDropdown";
import { useBuilderContentPermissions } from "@src/features/libraryItem/hooks/useBuilderContentPermissions";
import useActionPermissions from "@src/features/app/hooks/useActionPermissions";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@src/lib/ui/Tooltip";
import {
  PromptTab,
  useLibraryItemQueryParams,
} from "@src/features/libraryItem/contexts/LibraryItemQueryParamsContext";
import { Button } from "@src/lib/ui/Button";
import DotsIcon from "@src/lib/icons/18px/dots";
import CloudUploadOutlineIcon from "@src/lib/icons/18px/cloud-upload-outline";
import UsersOutlineIcon from "@src/lib/icons/18px/users-outline";
import CircleDottedCheckmarkIcon from "@src/lib/icons/18px/circle-dotted-checkmark";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@src/lib/ui/DropdownMenu";
import MessageOutlineIcon from "@src/lib/icons/18px/message-outline";

type Props = {
  translationModeEnabled?: boolean;
  setTranslationModeEnabled?: (enabled: boolean) => void;
  onShareClick?: () => void;
  shareDisabled?: boolean;
  hasPublishPermission?: boolean;
  hasCommentPermission?: boolean;
  onPublishClick?: () => void;
  publishDisabled?: boolean;
  publishLoading?: boolean;
  publishButtonText?: string;
  onAssignClick?: () => void;
  assignDisabled?: boolean;
  onPreviewClick?: () => void;
  extraButtons?: React.ReactNode;
  overflowItems?: React.ReactNode;
  selectedMainTab: string;
  hidePublishButton: boolean;
};

const LibraryItemDetailPageHeaderRight: FC<Props> = ({
  translationModeEnabled,
  setTranslationModeEnabled,
  shareDisabled,
  onShareClick,
  hasPublishPermission,
  hasCommentPermission,
  onPublishClick,
  publishDisabled,
  publishLoading,
  publishButtonText = "Publish",
  onAssignClick,
  assignDisabled,
  extraButtons,
  overflowItems,
  selectedMainTab,
  hidePublishButton,
}) => {
  const { userActionPermissions } = useActionPermissions();
  const { hasEditPermission } = useBuilderContentPermissions();
  const { selectedPromptTab, setPromptTab } = useLibraryItemQueryParams();
  return (
    <div className="flex flex-1">
      <div className="ml-auto flex items-center gap-1.5 self-stretch">
        {extraButtons}
        <BuilderActiveClientsStack />
        {selectedMainTab === "build" && setTranslationModeEnabled && (
          <TranslationDropdown
            translationModeEnabled={translationModeEnabled}
            setTranslationModeEnabled={setTranslationModeEnabled}
          />
        )}
        {selectedMainTab === "build" && hasCommentPermission && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant={
                    selectedPromptTab === PromptTab.Comments
                      ? "default"
                      : "ghost"
                  }
                  size="icon-md"
                  onClick={() => {
                    setPromptTab(
                      selectedPromptTab === PromptTab.Comments
                        ? PromptTab.Design
                        : PromptTab.Comments,
                    );
                  }}
                >
                  <MessageOutlineIcon />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">Comments</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        {hasEditPermission && onShareClick && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon-md"
                  disabled={shareDisabled}
                  onClick={onShareClick}
                >
                  <UsersOutlineIcon />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">Collaborators</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        {userActionPermissions?.assignContent && onAssignClick && (
          <Button
            size="md"
            variant="outline"
            disabled={assignDisabled}
            dataTestId="assign-course-button"
            onClick={onAssignClick}
          >
            <CircleDottedCheckmarkIcon />
            Assign
          </Button>
        )}
        {hasPublishPermission && !hidePublishButton && (
          <Button
            size="md"
            onClick={onPublishClick}
            disabled={publishDisabled || publishLoading}
            dataTestId="publish-button-header"
          >
            <CloudUploadOutlineIcon data-icon="inline-start" />
            {publishButtonText}
          </Button>
        )}
        {hasEditPermission && overflowItems && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon-md" aria-label="More actions">
                <DotsIcon />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>{overflowItems}</DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
};

export default LibraryItemDetailPageHeaderRight;
